import { DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Typography,
  Upload,
  message
} from "antd";
import FormItem from "antd/es/form/FormItem";
import { calculateJob, createJob } from "api/job";
import { useRef, useState } from "react";
import type { UploadProps } from "antd";
import classes from "../../components/Panel/ConsumerPanel/JobsOverview/Jobs.module.css";
import { Link } from "react-router-dom";
import { useUserType } from "hooks/useUserType";
import circleCheckIcon from "assets/circleCheckIcon.png";
import uploadFileIcon from "assets/fileUploadIcon.png";
import cancelIcon from "assets/cancel.png";
import { FormInstance } from "antd/lib";
import { VALIDATION } from "constants/strings";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useGetIdentity } from "@refinedev/core";
import { UserIdentityData } from "types/props";
import { authProvider } from "authProvider";

dayjs.extend(duration);

export const formatTime = (minutes: number) => {
  const totalSeconds = minutes * 60;
  const timeDuration = dayjs.duration(totalSeconds, "seconds");

  const hours = timeDuration.hours();
  const mins = timeDuration.minutes();
  const secs = timeDuration.seconds();

  if (hours > 0) {
    // If there are hours, show hours, minutes, and possibly seconds.
    return `${hours}h ${mins ? mins + "m" : ""} ${secs ? secs + "s" : ""}`.trim();
  } else if (mins > 0) {
    // If there are minutes (but no hours), show minutes and possibly seconds.
    return `${mins}m ${secs ? secs + "s" : ""}`.trim();
  } else {
    // If there are only seconds.
    return `${secs}s`;
  }
};

type Job = {
  job_type?: string;
  n_photons?: number;
  n_modes?: number;
  max_events?: number[];
  max_time_ms?: number[];
  job_name?: string;
  unitary?: number[][][];
  configurations?: any[];
  self_uri?: string;
  job_runs_uri?: string;
  createdAt?: Date;
};

type FileInvalid = {
  error: string;
  state: boolean;
};
const MessageResponseBody = ({
  messageData,
  index
}: {
  messageData: string;
  index: string | number;
}) => {
  return (
    <>
      <span style={{ marginRight: 8 }}>{messageData}</span>
      <span>
        <Button type="primary" danger onClick={() => message.destroy(index)}>
          Dismiss
        </Button>
      </span>
    </>
  );
};

const showErrorToast = (errorMessage: string) => {
  const errorkey = crypto.randomUUID();
  message.error({
    content: <MessageResponseBody messageData={errorMessage} index={errorkey} />,
    duration: 6,
    key: errorkey
  });
};

export const CreateJobModal: React.FC<{
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  fetchJobData: () => Promise<void>;
}> = ({ isModalOpen, setModalOpen, fetchJobData }) => {
  const { data: user, refetch } = useGetIdentity<UserIdentityData>();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>();
  const [invalidFile, setInvalidFile] = useState<FileInvalid>({
    error: "",
    state: false
  });
  const { isInternalUser } = useUserType();
  const [priority, setPriority] = useState(5);
  const formRef = useRef<FormInstance | null>(null);

  const handleCreateJob = async () => {
    if (!(jobData.job_name && jobData.job_type && jobData.n_photons && jobData.unitary)) {
      const errormsg = "Please enter job name and proper job file.";
      showErrorToast(errormsg);
      return;
    }

    // check if n_photons, n_modes, max_events, max_time_ms are are positive numbers and not alphabets or special characters or negative numbers
    Object.entries(jobData).forEach(([key, val], index) => {
      if (key === "n_photons" || key === "n_modes") {
        if (isNaN(Number(val)) || Number(val) < 0) {
          const errormsg = `Please enter positive integer values for ${key}`;
          showErrorToast(errormsg);
          return;
        }
      }
    });
    try {
      setIsLoading(true);
      const res = await createJob({
        ...jobData,
        max_events: [jobData.max_events],
        max_time_ms: [jobData.max_time_ms],
        priority: isInternalUser ? priority : undefined
      });

      if (res.success) {
        message.success("Job created successfully");
      }

      await fetchJobData();
      setIsLoading(false);
      setModalOpen(false);
      setResult(null);
      setJobData({
        job_type: undefined,
        n_photons: undefined,
        n_modes: undefined,
        max_events: undefined,
        job_name: undefined,
        configurations: undefined,
        self_uri: undefined,
        job_runs_uri: undefined
      });
      authProvider
        .check()
        .then(() => {
          refetch();
        })
        .catch();
    } catch (er: any) {
      const errormsg = "An error occurred while creating the Job.";
      showErrorToast(Array.isArray(er?.message) ? er?.message[0] : er?.message || errormsg);
      setIsLoading(false);
    }
  };
  const [jobData, setJobData] = useState<Job>({
    job_type: undefined,
    n_photons: undefined,
    n_modes: undefined,
    max_events: undefined,
    max_time_ms: undefined,
    job_name: undefined,
    unitary: undefined,
    configurations: undefined,
    self_uri: undefined,
    job_runs_uri: undefined
  });

  const compute = async () => {
    // terminate if file is invalid
    if (invalidFile.state) {
      const errormsg = invalidFile.error;
      showErrorToast(errormsg);
      resetData();
      return;
    }

    // job_type: BS
    if (jobData.job_type === "BS") {
      const validateData = new Promise(function (resolve, reject) {
        // Check if fields are not empty
        Object.entries(jobData).forEach(([fieldName, fieldValue], index) => {
          if (
            fieldName === "n_photons" ||
            fieldName === "n_modes" ||
            fieldName === "max_events" ||
            fieldName === "max_time_ms"
          ) {
            if (!fieldValue) {
              const errMessage = `Required field "${fieldName}" not found in JSON job file`;
              showErrorToast(errMessage);
              reject();
              return;
            }
          }
        });

        // check if n_photons, n_modes, max_events, max_time_ms are are positive numbers and not alphabets or special characters or negative numbers
        Object.entries(jobData).forEach(([key, val], index) => {
          if (key === "n_photons" || key === "n_modes") {
            if (
              val !== undefined &&
              (isNaN(Number(val)) ||
                Number(val) < 0 ||
                !Number.isInteger(Number(val)) ||
                String(val).indexOf("e") !== -1)
            ) {
              const errormsg = `Please enter positive integer values for ${key}`;
              showErrorToast(errormsg);
              reject();
              return;
            }
          }

          if (key === "max_events" || key === "max_time_ms") {
            if (!Array.isArray(val)) {
              const errormsg = `Please enter an array of numbers for ${key}`;
              showErrorToast(errormsg);
              reject();
            }

            if ((val as number[]).length === 0) {
              const errormsg = `${key} can not be an empty array.`;
              showErrorToast(errormsg);
              reject();
            }

            if ((val as number[]).some((v: number) => isNaN(v) || v < 0)) {
              const errormsg = `Please enter positive integer values for ${key}`;
              showErrorToast(errormsg);
              reject();
            }
          }
        });

        // check if both max_events and max_time_ms are 0
        if (
          jobData.max_events?.reduce((a, b) => a + b, 0) === 0 &&
          jobData.max_time_ms?.reduce((a, b) => a + b, 0) === 0
        ) {
          const errormsg =
            'Both "max_events" and "max_time_ms" can not be 0. Please provide a value more than 0 for at least one of the fields.';
          showErrorToast(errormsg);
          reject();
          return;
        }

        // limit max_events to 100,000
        if ((jobData.max_events || []).reduce((a, b) => a + b, 0) > 100000) {
          const errormsg = '"max_events" should not be more than 100,000';
          showErrorToast(errormsg);
          reject();
          return;
        }
        resolve(true);
      });
      try {
        // run if all validation conditions are satisfied
        const checkValidation = await validateData;
        if (checkValidation) {
          setIsLoading(true);
          try {
            const res = await calculateJob(
              jobData.n_photons + "",
              jobData.n_modes + "",
              jobData.max_events?.reduce((a, b) => a + b, 0) + "",
              jobData.job_type + ""
            );
            if (res.error) {
              const errorkey = crypto.randomUUID();
              message.error({
                content: <MessageResponseBody messageData={res.message} index={errorkey} />,
                duration: 6,
                key: errorkey
              });
            }
            setResult(res.data);
            setIsLoading(false);
          } catch (er: any) {
            const errormsg = "An error occurred. Computation unsuccessful.";
            showErrorToast(Array.isArray(er?.message) ? er?.message[0] : er?.message || errormsg);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // validation failed
        console.log("Job File: Data Error");
        return;
      }
    }
    // job_type: CU
    else if (jobData.job_type === "CU") {
      const validateData = new Promise(function (resolve, reject) {
        // check if job_name exists
        if (!jobData.job_name) {
          const errormsg = 'Field "job_name" is missing';
          showErrorToast(errormsg);
          reject();
          return;
        }
        // check if both max_events and max_time_ms are 0
        if (
          jobData.max_events?.reduce((a, b) => a + b, 0) === 0 &&
          jobData.max_time_ms?.reduce((a, b) => a + b, 0) === 0
        ) {
          const errormsg =
            'Both "max_events" and "max_time_ms" can not be 0. Please provide a value more than 0 for at least one of the fields.';
          showErrorToast(errormsg);
          reject();
          return;
        }

        // limit max_events to 100,000
        if ((jobData.max_events || []).reduce((a, b) => a + b, 0) > 100000) {
          const errormsg = '"max_events" should not be more than 100,000';
          showErrorToast(errormsg);
          reject();
          return;
        }

        resolve(true);
      });

      try {
        // run if all validation conditions are satisfied
        const checkValidation = await validateData;
        if (checkValidation) {
          setIsLoading(true);
          try {
            const res = await calculateJob(
              jobData.n_photons + "",
              jobData.n_modes + "",
              jobData.max_events?.reduce((a, b) => a + b, 0) + "",
              jobData.job_type + ""
            );
            if (res.error) {
              showErrorToast(res.message);
            }
            setResult(res.data);
            setIsLoading(false);
          } catch {
            const errormsg = "An error occurred. Computation unsuccessful.";
            showErrorToast(errormsg);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // validation failed
        console.log("Job File: Data Error");
        return;
      }
    } else {
      // job_type isn't defined
      const errormsg = 'Required field "job_type" not found in JSON job file';
      showErrorToast(errormsg);
      return;
    }
  };

  const props: UploadProps = {
    accept: "json",
    onChange() {
      // when another file is uploaded/file is changed
      resetData();
    },
    onRemove() {
      // when file is deleted/removed
      resetData();
    },
    beforeUpload(file, FileList) {
      setInvalidFile({
        error: "",
        state: false
      });

      if (file.type !== "application/json") {
        const errorMessage = "Please upload a valid JSON file";
        showErrorToast(errorMessage);

        setInvalidFile({
          error: `Invalid file. Can not run computation (Please upload a valid JSON file)`,
          state: true
        });
        return false;
      }

      const fileReader = new FileReader();
      fileReader.readAsText(file, "UTF-8");
      fileReader.onload = (e) => {
        try {
          const data = JSON.parse(e?.target?.result as string);
          let totalPhotons = 0;
          let total_max_events = 0;
          let total_max_time_ms = 0;
          const max_events_array = [];
          const max_time_ms = [];
          let noOfModes = 0;
          data.unitary.map((item: any) => {
            noOfModes = item.length;
          });

          if (!data.job_type) {
            const errorMessage = "'job_type' field is required.";
            showErrorToast(errorMessage);

            setInvalidFile({
              error: `Invalid file. Can not run computation (Required field "job_type" not found in JSON job file)`,
              state: true
            });
            return false;
          }

          const isCuJobsEnabled = process.env.REACT_APP_CU_JOBS_ENABLED === "true";

          if (!isCuJobsEnabled && data.job_type === "CU") {
            const errorMessage = "CU jobs are currently not supported";
            showErrorToast(errorMessage);

            setInvalidFile({
              error: `Cannot run computation (CU jobs are currently not supported)`,
              state: true
            });

            return false;
          }

          if (!["BS", "CU"].includes(data.job_type)) {
            const errorMessage = "Invalid job type. Please provide a valid job type (BS or CU)";
            showErrorToast(errorMessage);

            setInvalidFile({
              error: `Invalid file. Can not run computation (Please provide a valid job type (BS or CU))`,
              state: true
            });

            return false;
          }

          if (data.job_name && typeof data.job_name !== "string") {
            const errorMessage = "type of job_name should be string";
            showErrorToast(errorMessage);

            setInvalidFile({
              error: `Invalid file. Can not run computation (type of job_name should be string))`,
              state: false
            });

            return false;
          }

          const newJobName =
            jobData.job_name?.trim() || data.job_name?.trim() || `job_${new Date().getTime()}`;

          if (data.job_type === "CU") {
            if (!data.configurations || data.configurations.length === 0) {
              const errorMessage = "Configurations field is required for CU job type";
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation (Required field "configurations" not found in JSON job file)`,
                state: true
              });
              return false;
            }

            for (let i = 0; i < data.configurations?.length; i++) {
              if (data.configurations[i].max_events === undefined) {
                const errorMessage = `"max_events" is required in configurations[${i}]`;
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation (Required field "max_events" not found in configurations[${i}])`,
                  state: true
                });
                return false;
              }

              if (data.configurations[i].max_time_ms === undefined) {
                const errorMessage = `"max_time_ms" is required in configurations[${i}]`;
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation (Required field "max_time_ms" not found in configurations[${i}])`,
                  state: true
                });
                return false;
              }

              if (
                typeof data.configurations[i].max_events !== "number" ||
                typeof data.configurations[i].max_time_ms !== "number"
              ) {
                const errorMessage = `type of max_events or max_time_ms should be number.`;
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation (Please enter positive integer values for max_events or max_time_ms in configurations[${i}])`,
                  state: true
                });
                return false;
              }

              if (!data.configurations[i].input_photons) {
                const errorMessage = `"input_photons" is required in configurations[${i}]`;
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation (Required field "input_photons" not found in configurations[${i}])`,
                  state: true
                });
                return false;
              }

              if (!Array.isArray(data.configurations[i].input_photons)) {
                const errorMessage = `"input_photons" should be an array of numbers in configurations[${i}]`;
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation ("input_photons" should be an array in configurations[${i}])`,
                  state: true
                });
                return false;
              }

              if (data.configurations[i].input_photons.length === 0) {
                const errorMessage = `"input_photons" can not be empty array in configurations[${i}]`;
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation ("input_photons" can not be empty array in configurations[${i}])`,
                  state: true
                });
                return false;
              }

              for (let j = 0; j < data.configurations[i].input_photons?.length; j++) {
                if (typeof data.configurations[i].input_photons[j] !== "number") {
                  const errorMessage = `"input_photons" should be an array of numbers in configurations[${i}]`;
                  showErrorToast(errorMessage);

                  setInvalidFile({
                    error: `Invalid file. Can not run computation ("input_photons" should be an array of numbers in configurations[${i}])`,
                    state: true
                  });
                  return false;
                }
              }

              Object.entries(data.configurations[i]).forEach(([key, val], index) => {
                if (key === "max_events" || key === "max_time_ms") {
                  if (
                    Number(val) < 0 ||
                    !Number.isInteger(Number(val)) ||
                    String(val).indexOf("e") !== -1
                  ) {
                    const errormsg = `Please enter positive integer values for ${key}`;
                    showErrorToast(errormsg);
                    setInvalidFile({
                      error: `Invalid file. Can not run computation (Please enter positive integer values for ${key})`,
                      state: true
                    });
                    return;
                  }
                }
              });
              if (
                data.configurations[i].max_events === 0 &&
                data.configurations[i].max_time_ms === 0
              ) {
                const errormsg =
                  'Both "max_events" and "max_time_ms" can not be 0. Please provide a value more than 0 for at least one of the fields.';
                showErrorToast(errormsg);
                setInvalidFile({
                  error: `Invalid file. Can not run computation. (Both "max_events" and "max_time_ms" can not be 0)`,
                  state: true
                });
                return;
              }
              totalPhotons += data.configurations[i].input_photons?.length || 0;
              total_max_events += data.configurations[i].max_events || 0;
              max_events_array.push(data.configurations[i].max_events || 0);
              total_max_time_ms += data.configurations[i].max_time_ms || 0;
              max_time_ms.push(data.configurations[i].max_time_ms || 0);
            }
            setJobData({
              job_type: data.job_type,
              n_photons: totalPhotons,
              n_modes: noOfModes,
              max_events: max_events_array,
              max_time_ms: max_time_ms,
              unitary: data.unitary,
              configurations: data.configurations || [],
              job_name: newJobName,
              job_runs_uri: data.job_runs_uri,
              self_uri: data.self_uri,
              createdAt: data.createdAt
            });
          } else {
            if (data.n_photons === undefined) {
              const errorMessage = '"n_photons" is required for BS job type';
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation (Required field "n_photons" not found in JSON job file)`,
                state: true
              });
              return false;
            }

            if (typeof data.n_photons !== "number") {
              const errorMessage = "'n_photons' must be a number";
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation ('n_photons' must be a number)`,
                state: true
              });
              return false;
            }

            if (data.max_events === undefined) {
              const errorMessage = '"max_events" is required for BS job type';
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation (Required field "max_events" not found in JSON job file)`,
                state: true
              });
              return false;
            }

            if (!Array.isArray(data.max_events)) {
              const errorMessage = '"max_events" should be an array of numbers';
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation ("max_events" should be an array)`,
                state: true
              });
              return false;
            }

            for (let i = 0; i < data.max_events.length; i++) {
              if (typeof data.max_events[i] !== "number") {
                const errorMessage = '"max_events" should be an array of numbers';
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation ("max_events" should be an array of numbers)`,
                  state: true
                });
                return false;
              }
            }

            if (data.max_time_ms === undefined) {
              const errorMessage = '"max_time_ms" is required for BS job type';
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation (Required field "max_time_ms" not found in JSON job file)`,
                state: true
              });
              return false;
            }

            if (!Array.isArray(data.max_time_ms)) {
              const errorMessage = '"max_time_ms" should be an array of numbers';
              showErrorToast(errorMessage);

              setInvalidFile({
                error: `Invalid file. Can not run computation ("max_time_ms" should be an array)`,
                state: true
              });
              return false;
            }

            for (let i = 0; i < data.max_time_ms.length; i++) {
              if (typeof data.max_time_ms[i] !== "number") {
                const errorMessage = '"max_time_ms" should be an array of numbers';
                showErrorToast(errorMessage);

                setInvalidFile({
                  error: `Invalid file. Can not run computation ("max_time_ms" should be an array of numbers)`,
                  state: true
                });
                return false;
              }
            }

            setJobData({
              job_type: data.job_type,
              n_photons: data.n_photons,
              n_modes: noOfModes,
              max_events: data.max_events,
              max_time_ms: data.max_time_ms,
              unitary: data.unitary,
              configurations: data.configurations || [],
              job_name: newJobName,
              job_runs_uri: data.job_runs_uri,
              self_uri: data.self_uri,
              createdAt: data.createdAt
            });
          }

          formRef.current?.setFieldValue("job-name", newJobName);
          (async () => {
            try {
              await formRef.current?.validateFields();
            } catch (e) {}
          })();
        } catch (error: any) {
          const errorMessage = "There is a syntax error in the uploaded file." + error.message;
          setInvalidFile({
            error: errorMessage,
            state: true
          });
          showErrorToast(errorMessage);
        }
      };
      return false;
    },
    itemRender: (originNode, file, fileList) => {
      if (!fileList.length) {
        return originNode;
      }

      return (
        <div className={classes.fileList}>
          <div style={{ display: "grid", placeItems: "center" }}>
            <img src={circleCheckIcon} alt="circle-check-icon" />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column"
            }}>
            <Typography.Text
              style={{
                padding: 0,
                margin: 0,
                fontFamily: "Titillium Web",
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: "23.2px",
                color: "#F4F4F4"
              }}>
              {file.name}
            </Typography.Text>
            <Typography.Paragraph
              style={{
                padding: 0,
                margin: 0,
                fontFamily: "Titillium Web",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "23.2px",
                color: "#98A2B3"
              }}>
              {(file.size! / 1024).toFixed(2)} KB
            </Typography.Paragraph>
          </div>
          <Button
            onClick={() => {
              resetData();
              fileList.pop();
            }}
            type="text"
            style={{
              color: "red",
              marginLeft: "auto",
              marginTop: "auto",
              marginBottom: "auto"
            }}
            icon={<DeleteOutlined />}
          />
        </div>
      );
    }
  };

  const resetData = () => {
    setResult(null);
    setJobData({
      ...jobData,
      job_type: undefined,
      n_photons: undefined,
      n_modes: undefined,
      max_events: undefined,
      max_time_ms: undefined,
      unitary: undefined,
      configurations: undefined,
      self_uri: undefined,
      job_runs_uri: undefined
    });
    setInvalidFile({
      error: "",
      state: false
    });
  };

  const handleCancel = () => {
    setJobData({
      job_type: undefined,
      n_photons: undefined,
      n_modes: undefined,
      max_events: undefined,
      max_time_ms: undefined,
      job_name: undefined,
      configurations: undefined,
      self_uri: undefined,
      job_runs_uri: undefined,
      createdAt: new Date()
    });
    setIsLoading(false);
    setModalOpen(false);
    setResult(null);
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgBase: "#1A1D38"
        },
        components: {
          Modal: {
            contentBg: "#1A1D38",
            colorText: "#fff"
          }
        }
      }}>
      <Modal
        footer={null}
        open={isModalOpen}
        style={{ padding: "1rem" }}
        centered
        destroyOnClose
        closeIcon={<img src={cancelIcon} alt="cancel-icon" style={{ padding: "5px" }} />}
        onCancel={handleCancel}>
        <Row justify="space-between">
          <h1 className={classes.ModalTitle}>Create Job</h1>
        </Row>
        <Form ref={formRef} layout="vertical">
          <FormItem
            label="Job name"
            name="job-name"
            style={{ marginBottom: "20px" }}
            rules={[
              {
                validator: (_, value: string) => {
                  const trimmedValue = value?.trim() || "";

                  if (trimmedValue.length === 0) {
                    return Promise.reject(VALIDATION.fieldIsRequired);
                  }

                  if (trimmedValue.length < 4) {
                    return Promise.reject(
                      VALIDATION.getMinCharacterValidationMessage("Job name", 4)
                    );
                  }

                  if (trimmedValue.length > 255) {
                    return Promise.reject(
                      VALIDATION.getMaxCharacterValidationMessage("Job name", 256)
                    );
                  }

                  return Promise.resolve();
                }
              }
            ]}>
            <Input
              placeholder="Enter Job Name"
              style={{ width: "100%" }}
              value={jobData.job_name}
              onChange={(e) => {
                setJobData((prev: any) => ({
                  ...prev,
                  job_name: e.target.value
                }));
              }}
            />
          </FormItem>
          <Typography.Paragraph style={{ color: "#e7dbdb", marginBottom: "20px" }}>
            Upload the job for the process to begin. Please upload files which are in .json format.
            {user?.role.type !== "admin" && (
              <>
                {" More specifics can be found here "}
                <Link className={classes.link} to="/documentation">
                  link to documentation
                </Link>
              </>
            )}
          </Typography.Paragraph>
          <div style={{ color: "#fff", marginBottom: "20px" }}>
            <Upload
              {...props}
              style={{ width: "100% !important" }}
              maxCount={1}
              accept="application/json">
              {!jobData.job_type && !invalidFile.state && (
                <div className={classes.fileList}>
                  <div>
                    <img src={uploadFileIcon} alt="upload-file" />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column"
                    }}>
                    <Typography.Text
                      style={{
                        padding: 0,
                        margin: 0,
                        fontFamily: "Titillium Web",
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "23.2px",
                        color: "#FFF"
                      }}>
                      Upload your document
                    </Typography.Text>
                    <Typography.Paragraph
                      style={{
                        padding: 0,
                        margin: 0,
                        fontFamily: "Titillium Web",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "23.2px",
                        color: "#98A2B3"
                      }}>
                      JSON format
                    </Typography.Paragraph>
                  </div>

                  <Button
                    style={{
                      height: "36px",
                      marginLeft: "auto",
                      marginTop: "auto",
                      marginBottom: "auto",
                      fontWeight: 400,
                      fontSize: "14px",
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: "#283583"
                    }}>
                    Upload
                  </Button>
                </div>
              )}
            </Upload>
          </div>
        </Form>
        {result ? (
          <>
            <Row className={classes.estimate}>
              <Col span={24} style={{ marginBottom: "16px" }}>
                <Typography.Text
                  style={{
                    color: "#fff",
                    fontSize: "20px",
                    fontWeight: 700,
                    lineHeight: "32px",
                    letterSpacing: "-1.9%"
                  }}>
                  Job Estimate
                </Typography.Text>
              </Col>
              <Col span={24} className={classes.inputs}>
                <Flex gap={"24px"}>
                  <Col>
                    {" "}
                    <p className={classes.estimationHeading}>Estimated Run time </p>
                    <Typography.Paragraph>
                      <span className={classes.estimatedNumbers}>
                        {formatTime(result.executionTime / 60)}{" "}
                      </span>
                    </Typography.Paragraph>
                  </Col>

                  <Col
                    style={{
                      width: "1px",
                      border: "none",
                      backgroundColor: "#E5E9EB",
                      marginBottom: "28px",
                      marginTop: "8px"
                    }}
                  />

                  <Col>
                    <p className={classes.estimationHeading}>Estimated Cost</p>
                    <Typography.Paragraph>
                      <span className={classes.estimatedNumbers}>{result.credits || 0}</span>
                      <span className={classes.estimatedNumbersSubText}>credits</span>
                    </Typography.Paragraph>
                  </Col>
                </Flex>
              </Col>
            </Row>
            <div
              style={{
                display: "flex",
                width: "100%",
                gap: 4,
                marginTop: "24px",
                justifyContent: "center",
                alignItems: "center"
              }}>
              {isInternalUser && (
                <Flex align="middle" justify="center">
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}>
                    Priority:
                  </p>
                  <Select
                    variant="borderless"
                    defaultValue={priority}
                    onChange={(value) => setPriority(value)}>
                    <Select.Option value={2}>2</Select.Option>
                    <Select.Option value={5}>5</Select.Option>
                  </Select>
                </Flex>
              )}

              <Button
                style={{
                  width: "100%",
                  height: "44px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  border: "none"
                }}
                onClick={async () => {
                  try {
                    await formRef.current?.validateFields();
                  } catch (error) {
                    return;
                  }

                  await handleCreateJob();
                }}
                loading={isLoading}
                disabled={isLoading}>
                Create Job
              </Button>
            </div>
          </>
        ) : (
          <Flex align="middle" justify="center" style={{ marginTop: "16px" }}>
            <Button
              style={{
                padding: "8px 12px",
                width: "100%",
                height: "44px",
                fontSize: "16px",
                borderRadius: "8px",
                border: "none"
              }}
              onClick={async () => {
                try {
                  await formRef.current?.validateFields();
                } catch (error) {
                  return;
                }

                await compute();
              }}
              loading={isLoading}
              disabled={isLoading || invalidFile.state || !jobData.job_name || !jobData.job_type}>
              Run Computation
            </Button>
          </Flex>
        )}
      </Modal>
    </ConfigProvider>
  );
};
